import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';

import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import EditIcon from '@material-ui/icons/Edit';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItem from '@material-ui/core/ListItem';

import ConfirmContext from 'contexts/ConfirmContext';

import { guestsSumSeats } from 'utils/bookings/prepareBookingsData';

import { CANCEL_ACTIVITY_BOOKING } from 'graphql/mutations/booking';

import useStyles from './styles';

const UpcomingActivitiesDialog = ({
  open,
  activity,
  onClose,
  onCancel,
  isOnlyView
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [t] = useTranslation();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [cancelActivityBooking] = useMutation(CANCEL_ACTIVITY_BOOKING);

  const schedule = activity.schedule;
  const activityBookings = activity.activityBookings || activity.participants;
  const bookTime = activity.closestEvent || {
    startDate: activity.startingDate,
    startTime: schedule && schedule.startTime,
    duration: schedule && schedule.duration,
    timeType: schedule && schedule.timeType
  };

  if (Object.keys(activity).length === 0) {
    return null;
  }

  const activityCancellationHandler = id => {
    confirm({
      title: `Cancel Booking?`,
      confirmationText: 'Cancel Booking',
      cancellationText: 'Back'
    }).then(() => {
      cancelActivityBooking({ variables: { ids: [parseInt(id)] } }).then(() => {
        enqueueSnackbar(t('Activity booking was successfully canceled!'), {
          variant: 'success'
        });
        onClose(true);
      });
    });
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }}>
      <IconButton className={classes.closeBtn} onClick={onClose}>
        <HighlightOffIcon className={classes.closeIcon} />
      </IconButton>
      <DialogTitle
        id="alert-dialog-title"
        classes={{ root: classes.title }}
        disableTypography={true}
      >
        {activity.productCode} • {activity.title}
      </DialogTitle>
      <DialogContent classes={{ root: classes.contentRoot }}>
        <List className={classes.root}>
          <ListSubheader className={classes.subheader}>
            {t('Activity type')}
          </ListSubheader>
          <ListItem className={classes.listItem}>
            {activity.isOpen ? t('Open') : t('Private')}
          </ListItem>
          <ListSubheader className={classes.subheader}>
            {t('Participants')}
          </ListSubheader>
          <List>
            {activityBookings &&
              activityBookings.map((booking, index) => {
                return (
                  <ListItem key={index} className={classes.participantsItem}>
                    <Typography className={classes.participantName}>
                      {booking.guestFirstName} {booking.guestLastName}
                    </Typography>
                    <Typography className={classes.participantName}>
                      {booking.platform}
                    </Typography>
                    <Typography>{guestsSumSeats(booking.guests)}</Typography>
                    <div className={classes.actionsWrapper}>
                      <EditIcon
                        onClick={() =>
                          history.push(
                            `/activities-bookings/${booking.id}/edit`
                          )
                        }
                        className={classNames(
                          classes.actionIcon,
                          classes.editIcon
                        )}
                      />
                      <HighlightOffIcon
                        onClick={() => activityCancellationHandler(booking.id)}
                        className={classes.actionIcon}
                      />
                    </div>
                  </ListItem>
                );
              })}
            {!activityBookings ||
              (activityBookings.length === 0 && (
                <ListItem className={classes.participantsItem}>—</ListItem>
              ))}
          </List>
          <ListSubheader className={classes.subheader}>
            {t('Start time')}
          </ListSubheader>
          <ListItem className={classes.listItem}>
            {bookTime.startDate ? `${bookTime.startDate}` : '—'}
            {bookTime.startTime ? `, ${bookTime.startTime}` : ''}
          </ListItem>
          <ListSubheader className={classes.subheader}>
            {t('Duration')}
          </ListSubheader>
          <ListItem className={classes.listItem}>
            {bookTime.duration ? bookTime.duration : '—'} {bookTime.timeType}
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions
        classes={{
          root: classNames(classes.actionsRoot, {
            [classes.actionsRootOnlyView]: isOnlyView
          })
        }}
      >
        {!isOnlyView && (
          <div>
            <Button
              variant="contained"
              classes={{ root: classes.actionBtnRoot }}
              onClick={e => {
                e.stopPropagation();
                history.push(
                  `/activities/${
                    activity.activity ? activity.activity.id : activity.id
                  }`
                );
              }}
            >
              {t('Edit')}
            </Button>
            <Button
              variant="outlined"
              onClick={e => {
                e.stopPropagation();
                onCancel(activity);
              }}
              style={{ marginLeft: '8px' }}
              classes={{ root: classes.cancelBtnRoot }}
            >
              {t('Cancel all Activities')}
            </Button>
          </div>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          classes={{ root: classes.cancelBtnRoot }}
        >
          {t('Back')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpcomingActivitiesDialog;
